import React from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type ModernAttendanceChartProps = {
    // New: pre-computed values (Admin Dashboard)
    attendanceByBranch?: { branch: string; count: number }[];
    // Old: raw data array (Manager/Supervisor)
    attendance?: any[];
    loading?: boolean;
};

export const ModernAttendanceChart: React.FC<ModernAttendanceChartProps> = ({ attendanceByBranch, attendance, loading }) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

    // Compute from raw data if pre-computed values not provided
    let data: { branch: string; count: number }[] = [];
    if (attendanceByBranch) {
        data = attendanceByBranch;
    } else if (attendance) {
        const branchCounts: Record<string, number> = {};
        for (const a of attendance) {
            const branch = a.branchName || 'Unknown';
            branchCounts[branch] = (branchCounts[branch] || 0) + 1;
        }
        data = Object.entries(branchCounts)
            .map(([branch, count]) => ({ branch, count }))
            .sort((a, b) => b.count - a.count);
    }

    const categories = data.map(d => d.branch);
    const seriesData = data.map(d => d.count);

    const chartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            fontFamily: 'inherit',
            toolbar: {
                show: false
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                horizontal: true,
                barHeight: '40%',
                distributed: true
            }
        },
        colors: ['#7239ea', '#50cd89', '#009ef7', '#ffc700', '#f1416c'],
        dataLabels: {
            enabled: true,
            textAnchor: 'start',
            style: {
                colors: ['#fff']
            },
            formatter: function (val, opt) {
                return val + ""
            },
            offsetX: 0,
        },
        xaxis: {
            categories: categories,
            labels: {
                style: {
                    colors: '#a1a5b7',
                    fontSize: '12px'
                }
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDarkMode ? '#ffffff' : '#181c32',
                    fontSize: '13px',
                    fontWeight: 600
                },
                maxWidth: 200
            }
        },
        grid: {
            show: false,
            borderColor: '#eff2f5',
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 10
            }
        },
        tooltip: {
            theme: 'light',
            y: {
                title: {
                    formatter: function () {
                        return 'Workers: '
                    }
                }
            }
        },
        legend: {
            show: false
        }
    };

    return (
        <div className="card h-100" style={{ backgroundColor: isDarkMode ? '#1e1e2d' : '' }}>
            <div className="card-header border-0 pt-5">
                <h3 className="card-title align-items-start flex-column">
                    <span className={`card-label fw-bolder fs-3 ${isDarkMode ? 'text-white' : 'text-dark'}`}>Attendance Leaderboard</span>
                    <span className="text-muted mt-1 fw-bold fs-7">Top branches by presence</span>
                </h3>
            </div>
            <div className="card-body pt-3 pb-3">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{height: '250px'}}>
                        <span className="spinner-border text-primary"></span>
                    </div>
                ) : seriesData.length === 0 ? (
                    <div className="d-flex justify-content-center align-items-center h-200px text-gray-400 fw-bold">
                        No attendance data available
                    </div>
                ) : (
                    <div id="attendance_chart">
                        <Chart options={chartOptions} series={[{ name: 'Workers', data: seriesData }]} type="bar" height={250} />
                    </div>
                )}
            </div>
        </div>
    );
};
